import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { useSubscription } from '@/contexts/SubscriptionContext';
import LoginForm from '@/components/auth/LoginForm';
import LoginCard from '@/components/auth/LoginCard';
import ValidationAlert from '@/components/auth/ValidationAlert';
import { checkSubscriptionStatus, forceVerifySubscription } from '@/utils/subscription-checker';
import { supabase } from '@/integrations/supabase/client';

import { env } from '@/config/env';

const Login = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAutoLoginPending, setIsAutoLoginPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSubscription, isSubscriptionActive } = useSubscription();
  
  // Check if already logged in on component mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.user) {
          // User is already logged in, redirect to proper page
          handlePostLoginNavigation(data.session.user.email || '', data.session.user.id);
        } else {
          // Check if we need to perform auto-login after signup
          const state = location.state as any;
          if (state?.createdAccount && state?.email) {
            setIsAutoLoginPending(true);
            // Display message about confirming email and completing signup
            setVerificationMessage("Account created successfully. Please confirm your email address and then log in to complete your subscription.");
          }
        }
      } catch (error) {
        if (env.DEV) console.error('Error checking session:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkExistingSession();
  }, []);
  
  useEffect(() => {
    if (location.state && location.state.message) {
      setVerificationMessage(location.state.message);
    }
  }, [location]);

  const handlePostLoginNavigation = async (email: string, userId: string) => {
    try {
      if (env.DEV) console.log('Refreshing subscription data after login...');
      await refreshSubscription();
      
      // Check for special accounts that bypass subscription check
      const specialAccountEmails = import.meta.env.VITE_SPECIAL_ACCOUNTS?.split(',') || [];
      
      if (specialAccountEmails.includes(email)) {
        if (env.DEV) console.log('Special user detected - treating as subscribed');
        toast({
          title: "Login Successful",
          description: "Welcome back!",
          variant: "default"
        });
        navigate('/dashboard');
        return;
      }
      
      // Check if this is a redirect after signup (we need to proceed to checkout)
      const pendingSubscriptionPlanId = localStorage.getItem('pendingSubscriptionPlanId');
      const isCreatedAccount = location.state?.createdAccount;
      
      if (pendingSubscriptionPlanId) {
        if (env.DEV) console.log('Found pending subscription plan ID');
        
        // [REMOVED] Stripe checkout session logic
// Instead, after removing the pendingSubscriptionPlanId, always redirect to subscription plans
localStorage.removeItem('pendingSubscriptionPlanId');
if (env.DEV) console.log('Redirecting to subscription plans page after signup (Stripe logic removed)');
navigate('/subscription/plans');
return;
      } else if (isCreatedAccount) {
        // If account was just created but no pending plan ID, still go to subscription plans
        navigate('/subscription/plans');
        return;
      }
      
      // For existing users especially, do a thorough subscription verification
      if (env.DEV) console.log('Running full subscription verification');
      await forceVerifySubscription(userId);
      
      // Then check subscription status
      const hasActiveSubscription = await checkSubscriptionStatus(userId);
      
      if (!hasActiveSubscription) {
        if (env.DEV) console.log('Subscription inactive, redirecting to plans page');
        toast({
          title: "Subscription Required",
          description: "Please subscribe to access all features",
          variant: "default"
        });
        navigate('/subscription/plans');
        return;
      }
      
      if (env.DEV) console.log('Standard user login successful with active subscription');
      toast({
        title: "Login Successful",
        description: "Welcome back!",
        variant: "default"
      });
      navigate('/dashboard');
    } catch (error: any) {
      if (env.DEV) console.error('Error during post-login process');
      
      // Even if there's an error, prioritize subscription check
      navigate('/subscription/plans');
    }
  };

  const handleLoginSuccess = async (email: string) => {
    const { data } = await supabase.auth.getUser();
    if (data?.user) {
      await handlePostLoginNavigation(email, data.user.id);
    } else {
      if (env.DEV) console.error('Failed to get user ID after login');
      navigate('/subscription/plans');
    }
  };

  const handleLoginError = () => {
    // Handle login error if needed
    console.error('Login failed');
  };

  return (
    <LoginCard>
      {verificationMessage && (
        <ValidationAlert
          message={verificationMessage}
          variant="destructive"
          className="mb-4 bg-red-50 border-red-200 text-red-800"
        />
      )}
      {errorMessage && (
        <ValidationAlert
          message={errorMessage}
          className="mb-4"
        />
      )}
      {isCheckingAuth ? (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coaching-primary"></div>
        </div>
      ) : (
        <LoginForm 
          onLoginSuccess={handleLoginSuccess}
          onLoginError={handleLoginError}
          defaultEmail={location.state?.email || ''}
          autoFocus={!location.state?.email}
        />
      )}
    </LoginCard>
  );
};

export default Login;
